import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { LogOut, Mail, ClipboardList } from "lucide-react";
import { toast } from "sonner";

export const AgentDashboard = () => {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth?.access_token) return;
    setLoading(true);
    Promise.all([api.get(`/reservations`), api.get(`/contact`)])
      .then(([r, c]) => {
        setReservations(r.data);
        setContacts(c.data);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Load failed"))
      .finally(() => setLoading(false));
  }, [auth]);

  if (!auth || !["agent", "admin"].includes(auth.role))
    return <Navigate to="/agent/login" replace />;

  const doSignout = () => {
    logout();
    navigate("/agent/login");
  };

  return (
    <div data-testid="agent-dashboard" className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 bg-background/85 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-display text-xl font-semibold">
              Village<span className="text-accent">·</span>Farm<span className="text-accent">·</span>Food
            </Link>
            <span className="px-2.5 py-1 rounded-full bg-accent text-accent-foreground text-[10px] font-mono-ui uppercase tracking-widest">
              Agent
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-muted-foreground">
              Signed in as <strong>{auth.username}</strong>
            </span>
            <Button
              data-testid="agent-logout"
              onClick={doSignout}
              variant="outline"
              className="h-9 rounded-full"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 space-y-14">
        <section>
          <div className="flex items-center gap-3 mb-4">
            <ClipboardList className="w-5 h-5 text-accent" />
            <h2 className="font-display text-3xl font-semibold">
              Bulk Reservations
              <span className="ml-3 text-sm font-mono-ui text-muted-foreground align-middle">
                {reservations.length}
              </span>
            </h2>
          </div>

          {loading ? (
            <div className="h-24 rounded-xl bg-muted animate-pulse" />
          ) : reservations.length === 0 ? (
            <EmptyRow label="No reservations yet" />
          ) : (
            <div className="overflow-x-auto border border-border rounded-2xl bg-card">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-left">
                  <tr className="font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground">
                    <th className="py-3 px-4">Name</th>
                    <th className="py-3 px-4">Contact</th>
                    <th className="py-3 px-4">Product</th>
                    <th className="py-3 px-4">Qty</th>
                    <th className="py-3 px-4">Delivery</th>
                    <th className="py-3 px-4">Notes</th>
                    <th className="py-3 px-4">Received</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {reservations.map((r) => (
                    <tr key={r.id} data-testid={`reservation-${r.id}`}>
                      <td className="py-3 px-4 font-display font-semibold">{r.name}</td>
                      <td className="py-3 px-4">
                        <div>{r.email}</div>
                        <div className="text-xs text-muted-foreground">{r.phone}</div>
                      </td>
                      <td className="py-3 px-4 capitalize">{r.product_interest}</td>
                      <td className="py-3 px-4">{r.quantity}</td>
                      <td className="py-3 px-4">{r.delivery_date}</td>
                      <td className="py-3 px-4 text-muted-foreground max-w-[240px] truncate">
                        {r.notes || "—"}
                      </td>
                      <td className="py-3 px-4 font-mono-ui text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section>
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-5 h-5 text-accent" />
            <h2 className="font-display text-3xl font-semibold">
              Contact Messages
              <span className="ml-3 text-sm font-mono-ui text-muted-foreground align-middle">
                {contacts.length}
              </span>
            </h2>
          </div>
          {loading ? (
            <div className="h-24 rounded-xl bg-muted animate-pulse" />
          ) : contacts.length === 0 ? (
            <EmptyRow label="No messages yet" />
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {contacts.map((c) => (
                <article
                  key={c.id}
                  data-testid={`contact-${c.id}`}
                  className="rounded-2xl border border-border bg-card p-5"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-display text-lg font-semibold">{c.name}</div>
                    <div className="font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground">
                      {new Date(c.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">{c.email}</div>
                  <div className="mt-3 font-medium">{c.subject}</div>
                  <p className="mt-2 text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">
                    {c.message}
                  </p>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

const EmptyRow = ({ label }) => (
  <div className="border border-dashed border-border rounded-2xl py-10 text-center text-sm text-muted-foreground">
    {label}
  </div>
);
